import {
  addFamiliesResponseSchema,
  familiesResponseSchema,
  familyLinkCheckSchema,
  familySchema,
  inviteFamiliesResponseSchema,
  inviteLogSchema,
  linkFamiliesResponseSchema,
  okSchema,
  ownerHomeSchema,
  ownerOpenSpotsSchema,
  ownerTodoSchema,
  removedFamilySchema,
  sendNudgesResponseSchema,
  type AddFamiliesRequest,
  type AddFamiliesResponse,
  type AddFamilyAdultRequest,
  type AddFamilyChildRequest,
  type FamiliesBatch,
  type FamiliesResponse,
  type Family,
  type FamilyLinkCheck,
  type InviteFamiliesResponse,
  type InviteLog,
  type LinkFamiliesRequest,
  type LinkFamiliesResponse,
  type OwnerHome,
  type OwnerOpenSpots,
  type OwnerTodo,
  type RemovedFamily,
  type SendNudgesRequest,
  type SendNudgesResponse,
  type UpdateFamilyAdultRequest,
} from "@teamna/shared";
import { useMutation, useQuery, useQueryClient, type QueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { api, newIdempotencyKey } from "../api/client.ts";
import { queryKeys } from "./keys.ts";

/**
 * The Families page and the owner Home (2026-10-04), against `/v1/business/businesses/:id/...`.
 *
 * Both live under the `setup` query prefix: a family added here changes the setup checklist, the
 * placement list and the Home numbers, and every setup save (placing a child, a payment) changes
 * the families and the Home. One refresh of the prefix keeps all of them in step.
 */

const base = (businessId: string) => `/business/businesses/${businessId}`;

function refresh(queryClient: QueryClient, businessId: string): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.setup.all(businessId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.owner.members(businessId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
}

/** Writes one family into the cached list, so an edit shows at once. */
function putFamily(queryClient: QueryClient, businessId: string, family: Family): void {
  queryClient.setQueryData<FamiliesResponse>(queryKeys.setup.families(businessId), (current) =>
    current
      ? { ...current, families: current.families.map((row) => (row.id === family.id ? family : row)) }
      : current,
  );
}

/** GET /business/businesses/:id/families */
export function useFamilies(businessId: string | null): UseQueryResult<FamiliesResponse> {
  return useQuery({
    queryKey: queryKeys.setup.families(businessId ?? "none"),
    queryFn: () => api.get(`${base(businessId ?? "")}/families`, familiesResponseSchema),
    enabled: Boolean(businessId),
  });
}

/** POST /business/businesses/:id/families: one list. The answer carries the list id that Undo sends back. */
export function useAddFamilies(businessId: string): UseMutationResult<AddFamiliesResponse, unknown, AddFamiliesRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.post(`${base(businessId)}/families`, body, addFamiliesResponseSchema, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => refresh(queryClient, businessId),
  });
}

/** POST /business/businesses/:id/families/undo */
export function useUndoFamilies(businessId: string): UseMutationResult<{ ok: true }, unknown, FamiliesBatch> {
  const queryClient = useQueryClient();
  return useMutation({
    // Only the list's id: the server knows what the list made.
    mutationFn: (batch) => api.post(`${base(businessId)}/families/undo`, { batchId: batch.id }, okSchema),
    onSuccess: () => refresh(queryClient, businessId),
  });
}

type FamilyChange<T> = UseMutationResult<Family, unknown, T>;

function useFamilyChange<T extends { familyId: string }>(
  businessId: string,
  run: (input: T) => Promise<Family>,
): FamilyChange<T> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (family, input) => {
      // A family from the older import gets its account on the first change, so its id changes:
      // only the refetch can place it. Otherwise the edit shows at once, before the refetch.
      if (family.id === input.familyId) putFamily(queryClient, businessId, family);
      refresh(queryClient, businessId);
    },
  });
}

export function useUpdateFamilyAdult(businessId: string): FamilyChange<UpdateFamilyAdultRequest & { familyId: string; adultId: string }> {
  return useFamilyChange(businessId, ({ familyId, adultId, ...body }) =>
    api.patch(`${base(businessId)}/families/${familyId}/adults/${adultId}`, body, familySchema),
  );
}

export function useAddFamilyAdult(businessId: string): FamilyChange<AddFamilyAdultRequest & { familyId: string }> {
  return useFamilyChange(businessId, ({ familyId, ...body }) =>
    api.post(`${base(businessId)}/families/${familyId}/adults`, body, familySchema),
  );
}

export function useRemoveFamilyAdult(businessId: string): FamilyChange<{ familyId: string; adultId: string }> {
  return useFamilyChange(businessId, ({ familyId, adultId }) =>
    api.remove(`${base(businessId)}/families/${familyId}/adults/${adultId}`, familySchema),
  );
}

/** POST .../families/links/check: the check screen before a link. Changes nothing. */
export function useCheckFamilyLink(businessId: string): UseMutationResult<FamilyLinkCheck, unknown, [string, string]> {
  return useMutation({
    mutationFn: (familyIds) => api.post(`${base(businessId)}/families/links/check`, { familyIds }, familyLinkCheckSchema),
  });
}

/** POST .../families/links: one family goes into the other. The answer has the id that Undo sends back. */
export function useLinkFamilies(businessId: string): UseMutationResult<LinkFamiliesResponse, unknown, LinkFamiliesRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`${base(businessId)}/families/links`, body, linkFamiliesResponseSchema, { idempotencyKey: newIdempotencyKey() }),
    // The link may have been saved though the answer was lost: read the families again either way.
    onSettled: () => refresh(queryClient, businessId),
  });
}

/** POST .../families/links/:id/undo */
export function useUndoFamilyLink(businessId: string): UseMutationResult<Family, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId) => api.post(`${base(businessId)}/families/links/${linkId}/undo`, undefined, familySchema),
    onSettled: () => refresh(queryClient, businessId),
  });
}

export function useAddFamilyChild(businessId: string): FamilyChange<AddFamilyChildRequest & { familyId: string }> {
  return useFamilyChange(businessId, ({ familyId, ...body }) =>
    api.post(`${base(businessId)}/families/${familyId}/children`, body, familySchema),
  );
}

export function useUpdateFamilyChild(
  businessId: string,
): FamilyChange<{ familyId: string; participantId: string; name?: string; age?: number | null }> {
  return useFamilyChange(businessId, ({ familyId, participantId, ...body }) =>
    api.patch(`${base(businessId)}/families/${familyId}/children/${participantId}`, body, familySchema),
  );
}

export function useRemoveFamilyChild(businessId: string): FamilyChange<{ familyId: string; participantId: string }> {
  return useFamilyChange(businessId, ({ familyId, participantId }) =>
    api.remove(`${base(businessId)}/families/${familyId}/children/${participantId}`, familySchema),
  );
}

export function useRestoreFamilyChild(businessId: string): FamilyChange<{ familyId: string; participantId: string }> {
  return useFamilyChange(businessId, ({ familyId, participantId }) =>
    api.post(`${base(businessId)}/families/${familyId}/children/${participantId}/restore`, undefined, familySchema),
  );
}

/** PUT or DELETE /families/:id/hold. A null note takes the family off hold. */
export function useFamilyHold(businessId: string): FamilyChange<{ familyId: string; note: string | null }> {
  return useFamilyChange(businessId, ({ familyId, note }) =>
    note
      ? api.put(`${base(businessId)}/families/${familyId}/hold`, { note }, familySchema)
      : api.remove(`${base(businessId)}/families/${familyId}/hold`, familySchema),
  );
}

/** DELETE /families/:id. The answer is what Undo sends back. */
export function useRemoveFamily(businessId: string): UseMutationResult<RemovedFamily, unknown, { familyId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ familyId }) => api.remove(`${base(businessId)}/families/${familyId}`, removedFamilySchema),
    onSuccess: () => refresh(queryClient, businessId),
  });
}

export function useRestoreFamily(businessId: string): UseMutationResult<{ ok: true }, unknown, RemovedFamily> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (removed) => api.post(`${base(businessId)}/families/restore`, removed, okSchema),
    onSuccess: () => refresh(queryClient, businessId),
  });
}

/** POST /families/invite: every adult of the chosen families who is not in yet. */
export function useInviteFamilies(businessId: string): UseMutationResult<InviteFamiliesResponse, unknown, { familyIds: string[]; onlyNew?: boolean }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.post(`${base(businessId)}/families/invite`, body, inviteFamiliesResponseSchema, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => refresh(queryClient, businessId),
  });
}

/** GET /families/invites: the join link and the sent log. */
export function useInviteLog(businessId: string | null): UseQueryResult<InviteLog> {
  return useQuery({
    queryKey: queryKeys.setup.familyInvites(businessId ?? "none"),
    queryFn: () => api.get(`${base(businessId ?? "")}/families/invites`, inviteLogSchema),
    enabled: Boolean(businessId),
  });
}

/** GET /home: the five numbers and the people behind them. */
export function useOwnerHome(businessId: string | null): UseQueryResult<OwnerHome> {
  return useQuery({
    queryKey: queryKeys.setup.home(businessId ?? "none"),
    queryFn: () => api.get(`${base(businessId ?? "")}/home`, ownerHomeSchema),
    enabled: Boolean(businessId),
    staleTime: 30 * 1000,
  });
}

/** GET /home/open-spots: the free places of the next 48 hours, each session with its Book link (H2). */
export function useOpenSpots(businessId: string | null, enabled = true): UseQueryResult<OwnerOpenSpots> {
  return useQuery({
    queryKey: queryKeys.setup.homeOpenSpots(businessId ?? "none"),
    queryFn: () => api.get(`${base(businessId ?? "")}/home/open-spots`, ownerOpenSpotsSchema),
    enabled: Boolean(businessId) && enabled,
    staleTime: 30 * 1000,
  });
}

/**
 * GET /todo: the owner's To do list on Home, and the count on the Home menu item. Read again every
 * two minutes while Desk is open, so an answer from a parent shows without a reload.
 */
export function useOwnerTodo(businessId: string | null): UseQueryResult<OwnerTodo> {
  return useQuery({
    queryKey: queryKeys.todo.owner(businessId ?? "none"),
    queryFn: () => api.get(`${base(businessId ?? "")}/todo`, ownerTodoSchema),
    enabled: Boolean(businessId),
    staleTime: 30 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });
}

/** POST /nudges: messages from Home into each family's thread. */
export function useSendNudges(businessId: string): UseMutationResult<SendNudgesResponse, unknown, SendNudgesRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.post(`${base(businessId)}/nudges`, body, sendNudgesResponseSchema, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.setup.home(businessId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations });
    },
  });
}
